/**
 * Aliados e adversários: TSE candidatures, parties and global named entities the campaign counts
 * as allied or opposed (`api`'s `/campaigns/{id}/alliances`). Every post and comment analysis
 * reads the list as campaign facts.
 *
 * `AlliancesEditor` edits a list it is handed, so the record saves each change as it happens
 * (`AlliancesSection`) and the new-campaign dialog's third step saves once at the end. A chip
 * shows the TSE photo when the candidature has one stored, and the party or ballot number.
 */
import { Flag, Landmark, Plus, Users, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { PageSection } from '../../../chrome/page';
import { ErrorState } from '../../../chrome/state';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../ui/command';
import { IconButton } from '../../../ui/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { Skeleton } from '../../../ui/skeleton';
import { officeLabel } from '../tse/api/schemas';
import {
  useAllianceOptions,
  useAlliances,
  useAllianceSuggestions,
  useSaveAlliances,
} from './api/queries';
import { ALLIANCE_RELATIONS, type Alliance, type AllianceRelation } from './api/schemas';
import { rm } from './recordMessages';

const DEBOUNCE_MS = 250;

function keyOf(alliance: Pick<Alliance, 'relation' | 'name' | 'number'>): string {
  return `${alliance.relation}|${alliance.name}|${alliance.number ?? ''}`;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

function subtitleOf(alliance: Alliance): string {
  const number =
    alliance.number !== null ? rm('alliances.number', { number: alliance.number }) : null;
  if (alliance.kind === 'candidate') {
    return [alliance.party, number, alliance.office !== null ? officeLabel(alliance.office) : null]
      .filter((part) => part !== null)
      .join(' · ');
  }
  const kind = rm(
    alliance.kind === 'party'
      ? 'alliances.kind.party'
      : alliance.kind === 'organization'
        ? 'alliances.kind.organization'
        : 'alliances.kind.entity',
  );
  return [kind, number].filter((part) => part !== null).join(' · ');
}

function KindIcon(props: { kind: Alliance['kind'] }): ReactNode {
  const className = 'size-4';
  if (props.kind === 'party') return <Flag aria-hidden className={className} />;
  if (props.kind === 'organization') return <Landmark aria-hidden className={className} />;
  return <Users aria-hidden className={className} />;
}

export function AllianceAvatar(props: { alliance: Alliance; className?: string }): ReactNode {
  const { alliance } = props;
  return (
    <Avatar className={props.className ?? 'size-8'}>
      {alliance.photo_url !== null && (
        <AvatarImage
          src={alliance.photo_url}
          alt={rm('candidate.photo', { name: alliance.name })}
          className="object-cover"
        />
      )}
      <AvatarFallback className="text-caption">
        {alliance.kind === 'candidate' ? (
          initialsOf(alliance.name)
        ) : alliance.number !== null ? (
          <span data-numeric>{alliance.number}</span>
        ) : (
          <KindIcon kind={alliance.kind} />
        )}
      </AvatarFallback>
    </Avatar>
  );
}

function AllianceChip(props: { alliance: Alliance; onRemove?: () => void }): ReactNode {
  const { alliance } = props;
  return (
    <li className="flex min-w-0 items-center gap-2 rounded-full border bg-card py-1 pr-1 pl-1">
      <AllianceAvatar alliance={alliance} className="size-7" />
      <div className="flex min-w-0 flex-col pr-1">
        <span className="truncate text-label">{alliance.name}</span>
        <span className="truncate text-caption-sm text-muted-foreground">
          {subtitleOf(alliance)}
        </span>
      </div>
      {props.onRemove !== undefined && (
        <IconButton
          label={rm('alliances.remove', { name: alliance.name })}
          className="size-6 shrink-0"
          onClick={props.onRemove}
        >
          <X aria-hidden />
        </IconButton>
      )}
    </li>
  );
}

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
}

const GROUPS = [
  { key: 'candidate', label: 'alliances.group.candidate', kinds: ['candidate'] },
  { key: 'party', label: 'alliances.group.party', kinds: ['party'] },
  { key: 'entity', label: 'alliances.group.entity', kinds: ['organization', 'entity'] },
] as const;

function AllianceSearch(props: {
  campaignId: string;
  relation: AllianceRelation;
  taken: ReadonlySet<string>;
  onPick: (alliance: Alliance) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const options = useAllianceOptions(props.campaignId, debounced);
  const searching = debounced.trim().length >= 2;
  const label = rm(props.relation === 'ally' ? 'alliances.add.ally' : 'alliances.add.opponent');
  const items = (options.data ?? []).filter(
    (option) => !props.taken.has(keyOf({ ...option, relation: props.relation })),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="w-fit">
          <Plus aria-hidden />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-popover-sm p-0" aria-label={label}>
        {/* The list stays mounted and every state is a disabled item: see `TseCandidatePicker`. */}
        <Command shouldFilter={false}>
          <CommandInput
            aria-label={rm('alliances.search.label')}
            placeholder={rm('alliances.search.placeholder')}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {!searching && (
              <CommandGroup>
                <CommandItem value="__hint__" disabled>
                  {rm('alliances.search.hint')}
                </CommandItem>
              </CommandGroup>
            )}
            {searching && options.isPending && (
              <CommandGroup>
                <CommandItem
                  value="__loading__"
                  disabled
                  aria-label={rm('alliances.search.loading')}
                >
                  <Skeleton className="h-8 w-full" />
                </CommandItem>
              </CommandGroup>
            )}
            {searching && options.isError && (
              <CommandGroup>
                <CommandItem value="__error__" disabled>
                  {options.error.failure.kind === 'invalid'
                    ? options.error.failure.message
                    : rm('alliances.search.empty')}
                </CommandItem>
              </CommandGroup>
            )}
            {searching && options.isSuccess && items.length === 0 && (
              <CommandGroup>
                <CommandItem value="__empty__" disabled>
                  {rm('alliances.search.empty')}
                </CommandItem>
              </CommandGroup>
            )}
            {searching &&
              GROUPS.map((group) => {
                const rows = items.filter((item) =>
                  (group.kinds as readonly string[]).includes(item.kind),
                );
                if (rows.length === 0) return null;
                return (
                  <CommandGroup key={group.key} heading={rm(group.label)}>
                    {rows.map((option) => (
                      <CommandItem
                        key={keyOf({ ...option, relation: props.relation }) + option.kind}
                        value={keyOf({ ...option, relation: props.relation }) + option.kind}
                        onSelect={() => {
                          props.onPick({ ...option, relation: props.relation, detail: null });
                          setOpen(false);
                          setQuery('');
                        }}
                        className="gap-2"
                      >
                        <AllianceAvatar alliance={option} className="size-7" />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{option.name}</span>
                          <span className="truncate text-caption text-muted-foreground">
                            {option.detail ?? subtitleOf(option)}
                          </span>
                        </div>
                        {option.kind !== 'candidate' && option.number !== null && (
                          <span data-numeric className="ml-auto text-caption text-muted-foreground">
                            {option.number}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Both lists side by side above `md`, and the TSE's suggestions the campaign has not taken. */
export function AlliancesEditor(props: {
  campaignId: string;
  value: Alliance[];
  onChange: (next: Alliance[]) => void;
  editable: boolean;
}): ReactNode {
  const taken = new Set(props.value.map(keyOf));
  const suggestions = useAllianceSuggestions(props.campaignId, props.editable);
  const untaken = (suggestions.data ?? []).filter((item) => !taken.has(keyOf(item)));

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {ALLIANCE_RELATIONS.map((relation) => {
          const rows = props.value.filter((item) => item.relation === relation);
          return (
            <section
              key={relation}
              className="flex min-w-0 flex-col gap-2"
              aria-label={rm(`alliances.${relation}`)}
            >
              <h3 className="text-label text-muted-foreground">
                {rm('alliances.heading', {
                  label: rm(`alliances.${relation}`),
                  count: String(rows.length),
                })}
              </h3>
              {rows.length === 0 ? (
                <p className="text-body-sm text-muted-foreground">
                  {rm(relation === 'ally' ? 'alliances.empty.ally' : 'alliances.empty.opponent')}
                </p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {rows.map((alliance) => (
                    <AllianceChip
                      key={keyOf(alliance)}
                      alliance={alliance}
                      onRemove={
                        props.editable
                          ? () =>
                              props.onChange(
                                props.value.filter((item) => keyOf(item) !== keyOf(alliance)),
                              )
                          : undefined
                      }
                    />
                  ))}
                </ul>
              )}
              {props.editable && (
                <AllianceSearch
                  campaignId={props.campaignId}
                  relation={relation}
                  taken={taken}
                  onPick={(alliance) => props.onChange([...props.value, alliance])}
                />
              )}
            </section>
          );
        })}
      </div>
      {props.editable && untaken.length > 0 && (
        <section
          aria-label={rm('alliances.suggestions.title')}
          className="flex flex-col gap-2 rounded-md border border-dashed p-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-label text-muted-foreground">
              {rm('alliances.suggestions.title')}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => props.onChange([...props.value, ...untaken])}
            >
              <Plus aria-hidden />
              {rm('alliances.suggestions.add', { count: String(untaken.length) })}
            </Button>
          </div>
          <ul className="flex flex-wrap gap-2">
            {untaken.map((alliance) => (
              <li key={keyOf(alliance)}>
                <button
                  type="button"
                  onClick={() => props.onChange([...props.value, alliance])}
                  className="flex items-center gap-2 rounded-full border bg-surface-brief py-1 pr-3 pl-1 text-left hover:bg-secondary focus-visible:ring-3 focus-visible:ring-ring/50 outline-none"
                >
                  <AllianceAvatar alliance={alliance} className="size-6" />
                  <span className="flex flex-col text-caption leading-tight">
                    <span>{alliance.name}</span>
                    <span className="text-caption-sm text-muted-foreground">
                      {rm(`alliances.${alliance.relation}`)}
                    </span>
                  </span>
                  <Plus aria-hidden className="size-3.5 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The record's section: every add and remove is saved at once, like Contas e links. */
export function AlliancesSection(props: { campaignId: string; editable: boolean }): ReactNode {
  const alliances = useAlliances(props.campaignId);
  const save = useSaveAlliances(props.campaignId);
  return (
    <PageSection title={rm('alliances.title')} description={rm('alliances.description')}>
      {alliances.isPending ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : alliances.isError ? (
        <ErrorState failure={alliances.error.failure} onRetry={() => void alliances.refetch()} />
      ) : (
        <>
          {save.error !== null && <ErrorState failure={save.error.failure} />}
          <AlliancesEditor
            campaignId={props.campaignId}
            value={save.isPending && save.variables !== undefined ? save.variables : alliances.data}
            onChange={(next) => save.mutate(next)}
            editable={props.editable}
          />
        </>
      )}
    </PageSection>
  );
}
